import { useEffect, useState } from 'react';
import Modal from '../../../components/organisms/modal/Modal';
import * as DeprecatedColorEnum from '../../../enums/colorEnum';
import DeleteFileConfirmationModalEventManager, { DeleteFileConfirmationModalCustomEventType, DeleteFileConfirmationModalEventData } from '../modal/DeleteFileConfirmationModalEventManager';
import styles from './DeleteFileConfirmationModal.module.css';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import { Trans, useTranslation } from 'react-i18next';
import { classNames } from '../../utils';
import ProcessRequestFeedback from '../../molecules/process-request-feedback/ProcessRequestFeedback';
import ButtonNew from '../../molecules/button/Button';
import Notifications from '../notifications/Notifications';

interface DeleteFileConfirmationModalProps {
    readonly deleteCallback: (key: string, filename: string, id: string) => Promise<unknown>;
    readonly open?: boolean;
    readonly onDeleteSuccess?: () => void;
}

type DeleteFileConfirmationModalComponent = (props: DeleteFileConfirmationModalProps) => JSX.Element;

const deleteFileConfirmationModal = DeleteFileConfirmationModalEventManager.create<DeleteFileConfirmationModalEventData>('delete-file-confirmation-modal');

export const DeleteFileConfirmationModal = ({
    deleteCallback,
    open = false,
    onDeleteSuccess,
}: DeleteFileConfirmationModalProps) => {
    const { t } = useTranslation();

    const [ isOpen, setIsOpen ] = useState(open);
    const [ key, setKey ] = useState(null);
    const [ filename, setFilename ] = useState(null);
    const [ id, setId ] = useState(null);
    const [ isDeleting, setIsDeleting ] = useState(false);
    const [ isDeletingFile, setIsDeletingFile ] = useState(false);

    const setModal = (e: DeleteFileConfirmationModalCustomEventType) => {
        setIsOpen(e.detail.open);
        if (e.detail.key) {
            setKey(e.detail.key);
        }
        if (e.detail.filename) {
            setFilename(e.detail.filename);
        }
        if (e.detail.id) {
            setId(e.detail.id);
        }
    }

    useEffect(() => {
        deleteFileConfirmationModal?.addListener(setModal);

        return () => {
            deleteFileConfirmationModal?.removeListener(setModal);
        };
    }, []);

    const deleteFile = async () => {
        setIsDeleting(true);
        setIsDeletingFile(false);

        const startTime = Date.now();

        try {
            // TODO: make params configurable
            await deleteCallback(key, filename, id);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(300 - elapsedTime, 0);

            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setIsDeleting(false);
            setIsDeletingFile(true);

            await new Promise(resolve => setTimeout(resolve, 300 + 150));

            deleteFileConfirmationModal.close();

            await new Promise(resolve => setTimeout(resolve, 300));

            onDeleteSuccess?.();

            Notifications.add({
                type: 'success',
                message: (
                    <Trans
                        i18nKey="screening.incomeVerification.provideDocuments.deletion.modal.notification.success"
                        values={{ filename }}
                        components={{strong: <strong/>}}
                    />
                )
            });
        } catch (error) {
            Notifications.add({
                type: 'failed',
                message: t('screening.incomeVerification.provideDocuments.deletion.modal.notification.failed')
            });
        } finally {
            setIsDeleting(false);
            setIsDeletingFile(false);
        }
    }

    return (
        <Modal
            flowyColor={DeprecatedColorEnum.default.Error50}
            icon={'ri-delete-bin-line'}
            iconBackgroundColor={DeprecatedColorEnum.default.Error100}
            iconColor={DeprecatedColorEnum.default.Error700}
            isOpen={isOpen}
            manager={deleteFileConfirmationModal}
        >
            <div className={styles.gridMd}>
                <Text
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                    size={FontSizeEnum.Lg}
                    tag={'h2'}
                >{t('delete.file.confirmation.modal.title')}</Text>
                <Text tag='p'>{t('delete.file.confirmation.modal.p1')}</Text>
                <Text tag='p'>
                    <Trans
                        i18nKey="delete.file.confirmation.modal.p2"
                        values={{ filename }}
                        components={{strong: <strong/>}}
                    />
                </Text>
                <div className={classNames(styles.footer, (!isDeleting && !isDeletingFile) && styles.noColumnGap)}>
                    <ProcessRequestFeedback
                        isSubmitting={isDeleting}
                        isSuccessful={isDeletingFile}
                    />
                    <div className={styles.buttonsContainer}>
                        <ButtonNew
                            disabled={isDeleting || isDeletingFile}
                            variant='secondary-gray'
                            onClick={() => deleteFileConfirmationModal.close()}
                        >{t('delete.file.confirmation.modal.button.cancel')}</ButtonNew>
                        <ButtonNew
                            disabled={isDeleting || isDeletingFile}
                            variant='primary-destructive'
                            onClick={() => deleteFile()}
                        >{t('delete.file.confirmation.modal.button.delete')}</ButtonNew>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

Object.assign(DeleteFileConfirmationModal, deleteFileConfirmationModal);

export default (DeleteFileConfirmationModal as DeleteFileConfirmationModalComponent & DeleteFileConfirmationModalEventManager<DeleteFileConfirmationModalEventData>);
